import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { fetchComments } from '../store/slices/commentSlice'
import { fetchActivities } from '../store/slices/activitySlice'
import { Issue } from '@/types'
import Layout from '../components/Layout'
import IssueDetailModal from '../components/IssueDetailModal'
import * as issueApi from '../services/api/issue'

const IssueDetail = () => {
  const { issueId } = useParams<{ issueId: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { isAuthenticated } = useAppSelector(state => state.auth)
  const [issue, setIssue] = useState<Issue | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated) {
      // 如果未登入，重定向到登入頁面，登入後再回到此頁面
      navigate(`/login?redirect=/issues/${issueId}`)
      return
    }

    if (!issueId) {
      setError('議題 ID 無效')
      setLoading(false)
      return
    }

    // 載入議題詳細資料
    const loadIssue = async () => {
      try {
        setLoading(true)
        const response = await issueApi.getIssue(issueId)
        
        if (response.success && response.data?.issue) {
          setIssue(response.data.issue)
          
          // 載入相關資料
          dispatch(fetchComments(issueId))
          dispatch(fetchActivities(issueId))
        } else {
          setError('議題不存在或無法存取')
        }
      } catch (error) {
        console.error('載入議題失敗:', error)
        setError('載入議題失敗')
      } finally {
        setLoading(false)
      }
    }

    loadIssue()
  }, [issueId, isAuthenticated, navigate, dispatch])

  const handleClose = () => {
    // 如果有議題資料，嘗試跳轉到對應的專案看板
    if (issue?.project?.key) {
      navigate(`/project/${issue.project.key}/board`)
    } else {
      // 否則跳轉到首頁
      navigate('/')
    }
  }

  if (!isAuthenticated) {
    return null // 等待重定向
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    )
  }

  if (error || !issue) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {error || '議題不存在'}
          </h3>
          <p className="text-gray-500 mb-4">
            請檢查連結是否正確，或您是否有權限存取此議題
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            返回首頁
          </button>
        </div>
      </Layout>
    )
  }

  return (
    <>
      {/* 使用現有的 IssueDetailModal，但在頁面中顯示 */}
      <IssueDetailModal
        issue={issue}
        isOpen={true}
        onClose={handleClose}
      />
    </>
  )
}

export default IssueDetail